import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Key, Copy, ArrowRight, CreditCard, CalendarDays } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');
  const paypalProvider = searchParams.get('provider') === 'paypal';
  const paypalOrderId = searchParams.get('token'); // PayPal redirects with ?token=ORDER_ID&PayerID=...

  // PayPal capture flow
  const capturePaypal = useCallback(async (orderId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const cap = await axios.post(`${API}/payments/paypal/capture`, { order_id: orderId }, {
        headers, withCredentials: true,
      });
      if (cap.data.status === 'completed' || cap.data.status === 'already_paid') {
        setStatus('success');
        try {
          const r = await axios.get(`${API}/bookings`, { headers, withCredentials: true });
          const last = (r.data || [])[0];
          if (last && last.access_code) {
            setPaymentData({ booking: last, metadata: { type: 'booking' } });
          } else {
            setPaymentData({ metadata: { type: 'booking' } });
          }
        } catch { setPaymentData({ metadata: { type: 'booking' } }); }
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('PayPal capture error:', err);
      setStatus('error');
    }
  }, [token]);

  const pollPaymentStatus = useCallback(async (sid, attempts) => {
    const maxAttempts = 15;
    const pollInterval = 3000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/payments/status/${sid}`, {
        headers,
        withCredentials: true
      });

      const data = res.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Payment status error:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  }, [token]);

  useEffect(() => {
    if (paypalProvider && paypalOrderId) {
      capturePaypal(paypalOrderId);
      return;
    }
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollPaymentStatus(sessionId, 0);
  }, [sessionId, paypalProvider, paypalOrderId, pollPaymentStatus, capturePaypal]);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(t('payment.codeCopied'));
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full">
        {status === 'loading' && (
          <div className="text-center animate-fade-in-up" data-testid="payment-loading">
            <div className="w-16 h-16 rounded-full bg-[#F0EFEA] flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-[#1A3C34] animate-spin" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-[#1A3C34]">
              {t('payment.verifying')}
            </h1>
            <p className="mt-2 text-sm text-[#737373]">
              {t('payment.verifyingHint')}
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center animate-fade-in-up" data-testid="payment-success">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>

            {paymentData?.metadata?.type === 'subscription' ? (
              <>
                <h1 className="font-heading text-2xl font-semibold text-[#1A3C34]">
                  {t('payment.subscriptionConfirmed')}
                </h1>
                <p className="mt-2 text-sm text-[#737373]">
                  {t('payment.subscriptionConfirmedHint')}
                </p>

                <div className="mt-8 bg-[#1A3C34] rounded-md p-6" data-testid="subscription-confirmed">
                  <CreditCard className="w-8 h-8 text-white/60 mx-auto mb-3" />
                  <div className="text-xl text-white font-semibold">
                    {t(`subscriptions.planNames.${paymentData.metadata.plan_id}`, { defaultValue: paymentData.metadata.plan_id === 'annuel' ? t('payment.annualPlan') : t('payment.monthlyPlan') })}
                  </div>
                  {paymentData.subscription_details ? (
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-center gap-2 text-sm text-white/80">
                        <CalendarDays className="w-4 h-4" />
                        <span>{t('payment.starts')} : <strong>{paymentData.subscription_details.starts_at}</strong></span>
                      </div>
                      <div className="flex items-center justify-center gap-2 text-sm text-white/80">
                        <CalendarDays className="w-4 h-4" />
                        <span>{t('payment.ends')} : <strong>{paymentData.subscription_details.expires_at}</strong></span>
                      </div>
                      <div className="mt-3 text-sm text-[#C77D63] font-semibold">
                        -{paymentData.subscription_details.discount_percent}% {t('payment.discountAll')}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-white/60">
                      {t('payment.preferentialRates')}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <h1 className="font-heading text-2xl font-semibold text-[#1A3C34]">
                  {t('payment.paymentConfirmed')}
                </h1>
                <p className="mt-2 text-sm text-[#737373]">
                  {t('payment.bookingConfirmed')}
                </p>

                {paymentData?.booking?.access_code && (
                  <div className="mt-8 bg-[#1A3C34] rounded-md p-6" data-testid="payment-access-code">
                    <div className="text-white/60 text-xs mb-2 flex items-center justify-center gap-1">
                      <Key className="w-3 h-3" />
                      {t('payment.yourAccessCode')}
                    </div>
                    <div className="access-code text-3xl text-white tracking-wider">
                      {paymentData.booking.access_code}
                    </div>
                    <button
                      data-testid="copy-access-code-btn"
                      onClick={() => copyCode(paymentData.booking.access_code)}
                      className="mt-3 flex items-center gap-1 mx-auto text-white/70 hover:text-white text-xs transition-colors"
                    >
                      <Copy className="w-3 h-3" />
                      {t('payment.copyCode')}
                    </button>
                    <div className="mt-4 text-xs text-white/40">
                      {(paymentData.booking.space_name || t(`spaces.names.${paymentData.booking.space_id}`, { defaultValue: paymentData.booking.space_id }))} | {paymentData.booking.date} | {paymentData.booking.start_time} - {paymentData.booking.end_time}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="mt-8 flex flex-col gap-3">
              <Button
                data-testid="go-to-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="w-full h-12 bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 rounded-md font-medium"
              >
                {t('payment.viewDashboard')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                data-testid="book-another-btn"
                variant="outline"
                onClick={() => navigate('/plan')}
                className="w-full h-12 border-[#1A3C34] text-[#1A3C34] hover:bg-[#F0EFEA] rounded-md"
              >
                {t('payment.bookAnother')}
              </Button>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div className="text-center animate-fade-in-up" data-testid="payment-error">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${status === 'timeout' ? 'bg-amber-100' : 'bg-red-100'}`}>
              {status === 'timeout' ? (
                <Loader2 className="w-8 h-8 text-amber-600" />
              ) : (
                <XCircle className="w-8 h-8 text-red-600" />
              )}
            </div>
            <h1 className="font-heading text-2xl font-semibold text-[#1A3C34]">
              {status === 'timeout' ? t('payment.verifyingTitle') : t('payment.errorTitle')}
            </h1>
            <p className="mt-2 text-sm text-[#737373]">
              {status === 'timeout' ? t('payment.timeoutHint') : t('payment.errorHint')}
            </p>
            <div className="mt-8 flex flex-col gap-3">
              <Button
                data-testid="retry-btn"
                onClick={() => navigate('/plan')}
                className="w-full h-12 bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 rounded-md"
              >
                {t('payment.retry')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full h-12 border-[#1A3C34] text-[#1A3C34] hover:bg-[#F0EFEA] rounded-md"
              >
                {t('payment.dashboard')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
